import React, { useState, useRef, useEffect } from "react";
import { Link, useLocation } from "react-router-dom";

const Header = () => {
  const [dropdown, setDropdown] = useState("");
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const navRef = useRef();
  const location = useLocation();
  const [showSearch, setShowSearch] = useState(false);
  const [searchValue, setSearchValue] = useState("");
  const dropdownTimeout = useRef();

  const siteContent = [
    { title: "Home", description: "FOZZIL homepage", path: "/" },
    {
      title: "ZYPHER AI",
      description: "AI for healthcare",
      path: "/zypherai",
    },
    {
      title: "FOZZIL Intelligence",
      description: "Next level AI system",
      path: "/fozzil-intelligence",
    },
    {
      title: "N.I.S.T.I.A",
      description: "Smart glasses for the visually impaired",
      path: "/nistia",
    },
    { title: "AWM", description: "Research: AWM", path: "/awm" },
    { title: "Contact", description: "Contact FOZZIL", path: "/contact" },
    { title: "About Us", description: "About FOZZIL", path: "/about" },
    {
      title: "Ethics and Compliance",
      description: "Ethics and compliance at FOZZIL",
      path: "/ethics-compliance",
    },
    {
      title: "Terms of Use",
      description: "FOZZIL Terms of Use",
      path: "/terms-of-use",
    },
    {
      title: "Privacy Policy",
      description: "FOZZIL Privacy Policy",
      path: "/privacy-policy",
    },
  ];

  const filteredResults = searchValue.trim()
    ? siteContent.filter(
        (item) =>
          item.title.toLowerCase().includes(searchValue.toLowerCase()) ||
          item.description.toLowerCase().includes(searchValue.toLowerCase())
      )
    : [];

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (navRef.current && !navRef.current.contains(event.target)) {
        setDropdown("");
        setIsMobileMenuOpen(false);
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  const handleDropdown = (item) => {
    setDropdown(dropdown === item ? "" : item);
  };

  const toggleMobileMenu = () => {
    setIsMobileMenuOpen(!isMobileMenuOpen);
  };

  const isNavItemActive = (navItem) => {
    switch (navItem) {
      case "home":
        return location.pathname === "/";
      case "products":
        return (
          location.pathname === "/zypherai" ||
          location.pathname === "/fozzil-intelligence" ||
          location.pathname === "/nistia"
        );
      case "research":
        return (
          location.pathname.startsWith("/research") ||
          location.pathname === "/research/awm"
        );
      case "company":
        return (
          location.pathname.startsWith("/company") ||
          location.pathname === "/terms-of-use" ||
          location.pathname === "/privacy-policy" ||
          location.pathname === "/ethics-compliance" ||
          location.pathname === "/contact" ||
          location.pathname === "/about"
        );
      default:
        return false;
    }
  };

  return (
    <>
      <header className="bg-black text-white sticky top-0 z-[1000] py-2 pb-0.5">
        <div className="flex justify-between items-center w-full pl-0 pr-6 m-0 max-w-none min-h-[60px]">
          <div className="ml-0 pl-6 flex-none">
            <Link to="/">
              <img
                src={process.env.PUBLIC_URL + "/logo.png"}
                alt="FOZZIL Logo"
                className="w-[120px] h-auto block md:w-[150px]"
              />
            </Link>
          </div>
          <button
            className="block md:hidden bg-transparent border-none cursor-pointer p-2 z-[1001] relative"
            onClick={toggleMobileMenu}
          >
            <span
              className={`block relative w-6 h-[2px] bg-white transition-all duration-300
                before:content-[''] before:absolute before:w-6 before:h-[2px] before:bg-white before:transition-all before:duration-300
                after:content-[''] after:absolute after:w-6 after:h-[2px] after:bg-white after:transition-all after:duration-300
                ${
                  isMobileMenuOpen
                    ? "bg-transparent before:rotate-45 before:translate-y-0 after:-rotate-45 after:translate-y-0"
                    : "before:-translate-y-1.5 after:translate-y-1.5"
                }`}
            ></span>
          </button>
          <div
            className={
              `items-center` +
              // Mobile: show sidebar if open, hide if not
              (isMobileMenuOpen
                ? " flex fixed top-0 right-0 w-4/5 max-w-[400px] h-screen bg-black flex-col p-20 pt-20 transition-all duration-300 z-[1000] shadow-2xl"
                : " hidden") +
              // Desktop: always show as row
              " md:flex md:static md:flex-row md:p-0 md:w-auto md:h-auto md:bg-transparent md:shadow-none"
            }
          >
            <nav className="flex" ref={navRef}>
              <ul className="flex flex-col gap-1 md:flex-row md:gap-7 md:items-center m-0 p-0">
                <li
                  className={`nav-item${
                    isNavItemActive("home") ? " active" : ""
                  }`}
                >
                  <Link to="/" onClick={() => setIsMobileMenuOpen(false)}>
                    <span className="relative inline-block font-secondary text-[1.08rem] text-white px-1 py-0.5 font-normal tracking-wide cursor-pointer transition-colors duration-200 md:font-medium md:hover:text-blue-400">
                      Home
                      {isNavItemActive("home") && (
                        <span className="absolute left-0 right-0 -bottom-1 mx-auto w-16 h-0.5 bg-white rounded transition-all duration-200"></span>
                      )}
                    </span>
                  </Link>
                </li>
                <li
                  className={`nav-item${
                    isNavItemActive("products") ? " active" : ""
                  } relative`}
                  onClick={() => handleDropdown("products")}
                  onMouseEnter={() => {
                    clearTimeout(dropdownTimeout.current);
                    setDropdown("products");
                  }}
                  onMouseLeave={() => {
                    dropdownTimeout.current = setTimeout(
                      () => setDropdown(""),
                      180
                    );
                  }}
                >
                  <span className="relative inline-block font-secondary text-[1.08rem] text-white px-1 py-0.5 font-normal tracking-wide cursor-pointer transition-colors duration-200 md:font-medium md:hover:text-blue-400">
                    Products
                    {isNavItemActive("products") && (
                      <span className="absolute left-0 right-0 -bottom-1 mx-auto w-16 h-0.5 bg-white rounded transition-all duration-200"></span>
                    )}
                  </span>
                  {dropdown === "products" && (
                    <ul className="absolute left-0 top-full mt-2 min-w-[180px] bg-black text-gray-200 rounded-lg shadow-lg z-50 py-1 transition-all duration-150">
                      <li
                        className={
                          location.pathname === "/zypherai" ? "active" : ""
                        }
                      >
                        <Link
                          to="/zypherai"
                          className="dropdown-item block px-3 py-2 md:hover:bg-gray-800 rounded relative"
                          onClick={() => setIsMobileMenuOpen(false)}
                        >
                          <span className="relative inline-block">
                            ZYPHER AI
                            {location.pathname === "/zypherai" && (
                              <span className="absolute left-0 right-0 -bottom-1 mx-auto w-16 h-0.5 bg-white rounded transition-all duration-200"></span>
                            )}
                          </span>
                        </Link>
                      </li>
                      <li
                        className={
                          location.pathname === "/fozzil-intelligence"
                            ? "active"
                            : ""
                        }
                      >
                        <Link
                          to="/fozzil-intelligence"
                          className="dropdown-item block px-3 py-2 md:hover:bg-gray-800 rounded relative"
                          onClick={() => setIsMobileMenuOpen(false)}
                        >
                          <span className="relative inline-block">
                            FOZZIL INTELLIGENCE
                            {location.pathname === "/fozzil-intelligence" && (
                              <span className="absolute left-0 right-0 -bottom-1 mx-auto w-16 h-0.5 bg-white rounded transition-all duration-200"></span>
                            )}
                          </span>
                        </Link>
                      </li>
                      <li
                        className={
                          location.pathname === "/nistia" ? "active" : ""
                        }
                      >
                        <Link
                          to="/nistia"
                          className="dropdown-item block px-3 py-2 md:hover:bg-gray-800 rounded relative"
                          onClick={() => setIsMobileMenuOpen(false)}
                        >
                          <span className="relative inline-block">
                            N.I.S.T.I.A
                            {location.pathname === "/nistia" && (
                              <span className="absolute left-0 right-0 -bottom-1 mx-auto w-16 h-0.5 bg-white rounded transition-all duration-200"></span>
                            )}
                          </span>
                        </Link>
                      </li>
                    </ul>
                  )}
                </li>
                <li
                  className={`nav-item${
                    isNavItemActive("research") ? " active" : ""
                  } relative`}
                  onClick={() => handleDropdown("research")}
                  onMouseEnter={() => {
                    clearTimeout(dropdownTimeout.current);
                    setDropdown("research");
                  }}
                  onMouseLeave={() => {
                    dropdownTimeout.current = setTimeout(
                      () => setDropdown(""),
                      180
                    );
                  }}
                >
                  <span className="relative inline-block font-secondary text-[1.08rem] text-white px-1 py-0.5 font-normal tracking-wide cursor-pointer transition-colors duration-200 md:font-medium md:hover:text-blue-400">
                    Research
                    {isNavItemActive("research") && (
                      <span className="absolute left-0 right-0 -bottom-1 mx-auto w-16 h-0.5 bg-white rounded transition-all duration-200"></span>
                    )}
                  </span>
                  {dropdown === "research" && (
                    <ul className="absolute left-0 top-full mt-2 min-w-[180px] bg-black text-gray-200 rounded-lg shadow-lg z-50 py-1 transition-all duration-150">
                      <li
                        className={location.pathname === "/awm" ? "active" : ""}
                      >
                        <Link
                          to="/awm"
                          className="dropdown-item block px-3 py-2 md:hover:bg-gray-800 rounded relative"
                          onClick={() => setIsMobileMenuOpen(false)}
                        >
                          <span className="relative inline-block">
                            AWM
                            {location.pathname === "/awm" && (
                              <span className="absolute left-0 right-0 -bottom-1 mx-auto w-16 h-0.5 bg-white rounded transition-all duration-200"></span>
                            )}
                          </span>
                        </Link>
                      </li>
                    </ul>
                  )}
                </li>
                {/* Remove the <li> for Company and its dropdown from the nav list */}
              </ul>
            </nav>
            {/* Pillar divider between nav and search icon */}
            <div className="hidden md:block h-8 w-[2px] bg-white mx-4"></div>
            {/* End pillar divider */}
            <div className="flex items-center gap-3 w-auto md:w-full md:justify-center md:gap-7">
              <span
                className="text-white opacity-95 hover:text-blue-400 hover:opacity-100 cursor-pointer flex items-center"
                title="Search"
                onClick={(e) => {
                  e.stopPropagation();
                  setShowSearch((prev) => !prev);
                }}
              >
                <svg
                  width="22"
                  height="22"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth="2"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  viewBox="0 0 24 24"
                >
                  <circle cx="11" cy="11" r="8" />
                  <line x1="21" y1="21" x2="16.65" y2="16.65" />
                </svg>
              </span>
              {showSearch && (
                <div
                  className="relative"
                  onClick={(e) => e.stopPropagation()}
                  onMouseDown={(e) => e.stopPropagation()}
                >
                  <input
                    className="ml-2 px-3 py-1 rounded border border-gray-300 text-sm bg-gray-100 text-gray-900 focus:outline-none focus:ring-2 focus:ring-blue-400"
                    type="text"
                    autoFocus
                    value={searchValue}
                    onChange={(e) => setSearchValue(e.target.value)}
                    placeholder="Search..."
                    onMouseDown={(e) => e.stopPropagation()}
                  />
                  {filteredResults.length > 0 && (
                    <ul className="absolute top-9 left-2 right-0 bg-white text-gray-900 border border-gray-300 rounded z-[1002] list-none m-0 p-0 min-w-[220px] shadow-lg">
                      {filteredResults.map((item) => (
                        <li
                          key={item.path}
                          className="px-4 py-2 cursor-pointer hover:bg-gray-200"
                          onClick={() => {
                            setShowSearch(false);
                            setSearchValue("");
                            window.location.href = item.path;
                          }}
                          onMouseDown={(e) => e.stopPropagation()}
                        >
                          <div className="font-semibold">{item.title}</div>
                          <div className="text-xs text-gray-500">
                            {item.description}
                          </div>
                        </li>
                      ))}
                    </ul>
                  )}
                </div>
              )}
              <button
                className="bg-[#171717] text-white border-none rounded-full py-2 px-6 text-[1.03rem] font-secondary font-medium cursor-pointer ml-2 shadow-sm hover:bg-gray-700 hover:text-white hover:shadow-md transition-colors duration-200"
                onClick={() =>
                  window.open(
                    "https://intelligence.fozzil.in",
                    "_blank",
                    "noopener,noreferrer"
                  )
                }
              >
                Login
              </button>
            </div>
          </div>
        </div>
      </header>
    </>
  );
};

export default Header;
